/**
 *
 * OnDemandSettlements
 *
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import moment from 'moment';

import { Conditional, Button, Text } from '@cashfree-intl/coherent';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';

import {
  roundOffNumber,
  addCurrencySymbol,
  useQuery,
  getItemFromLocalStorage,
  copyToClipboard,
  titleCase,
  numberWithCommas,
} from 'utils/common';

import {
  TX_STATUS_CLASSNAME,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_OPTIONS,
  ON_DEMAND,
  SETTLE_NOW,
  SETTLE_NOW_SUCCESS,
  SETTLE_NOW_ERROR,
  DATE_AND_TIME_FORMAT,
  IN_PROGRESS,
  CURRENCY_SYMBOL,
  // FAILED,
  PLACEHOLDER_TEXT,
  SETTLEMENTS,
  NUDGE_BUTTON_ID,
  ON_DEMAND_FREE_TRIAL_ACTIVE_STATUS,
} from 'utils/constants';

import PaginatedTable from 'components/PaginatedTable';
import { formatColumns } from 'components/PaginatedTable/helpers';

import EVENTS from '../../constants/analytics';
import Analytics from '../../utils/analytics';

import Loader from '../../components/Loader';
import CustomIcon from '../../components/CustomIcons';

import OnDemandBanner from './components/OnDemandBanner';
import SettleNowModal from './components/SettleNowModal';
import SettleNowSuccessModal from './components/SettleNowSuccessModal';

import makeSelectOnDemandSettlements from './selectors';
import reducer from './reducer';
import saga from './saga';

import {
  enableLoaderAction,
  searchSettlementsAction,
  updateFiltersAction,
  resetFiltersAction,
  resetPageAction,
  getSettlementsHolidaysAction,
  resetDataAction,
  getOnDemandSettlementStatusAction,
  requestOnDemandActivationAction,
  getOnDemandMaxDailyLimitAction,
  createOnDemandSettlementsAction,
  getEligibleFundsAction,
  getSettlementRatesAction,
  disableLoaderAction,
  extendFreeTrialAction,
  getFreeTrialNudgeAction,
  getFreeTrialStatusAction,
  updateFreeTrialStatusAction,
  getOnDemandSettleNowDataAction,
  getFreeTrialConstantsAction,
  getUnsettledAmountAction,
} from './actions';
import {
  ACTIVE,
  DISABLED,
  REJECTED,
  NOT_REQUESTED,
  SETTLE_NOW_STATUS_MAPPER,
  ON_DEMAND_LIMIT,
  MINIMUM_ELIGIBLE_AMOUNT,
  ON_DEMAND_TEST_MID,
  ON_DEMAND_TEST_MINIMUM_ELIGIBLE_AMOUNT,
  HEADINGS,
  SOFT_APPROVED,
  ACTIVATION_MODAL,
  KNOW_MORE_URL,
} from './constants';

import DashboardFilters from '../DashboardFilters';
// import OnDemandSettlements from '../OnDemandSettlements';

import {
  showToast as showToastAction,
  resetTwoFactorAuthentication as resetTwoFactorAuthenticationValue,
} from '../Toast/actions';
// import { twoFactorAuthenticationSelector } from '../Toast/selectors';

import ActivationModal from './components/ActivationModal';
import EndFreeTrial from './components/EndFreeTrial';
import NudgeAlert from '../../components/NudgeAlert';
import OnDemandStatusBanner from './components/OnDemandStatusBanner';
import OnDemandSummaryCard from './components/OnDemandSummaryCard';
import { getOnDemandStatus } from './util';
// import { MerchantContext } from '../../contexts/MerchantContext';

export const showOnDemandStatusBanner = time =>
  moment().diff(
    moment(time)
      .clone()
      .add(3, 'days'),
  ) < 0;

export function getNudgeObj(response) {
  const {
    headerIconURL,
    headerText,
    buttonText,
    buttonId,
    label,
    headerColor,
    description,
    footer,
  } = response;
  // preparing the result object
  const result = {
    buttonText,
    buttonId,
    label,
    description,
    iconSrc: headerIconURL,
    title: headerText,
    metaData: footer,
  };
  // computing the text color for the title
  if (headerColor === 'success') result.success = true;
  else if (headerColor === 'alert') result.alert = true;
  else result.white = true;
  return result;
}
export function OnDemandSettlements({
  onDemandSettlements,
  searchSettlements,
  enableLoader,
  disableLoader,
  updateFilters,

  resetPage,
  resetFilters,
  twoFactorAuthentication,
  resetTwoFactorAuthentication,
  getSettlementsHolidays,
  resetData,
  getOnDemandSettlementStatus,
  requestOnDemandActivation,
  getOnDemandMaxDailyLimit,
  createOnDemandSettlements,
  getEligibleFunds,
  getSettlementRates,
  showToast,
  extendFreeTrial,
  getFreeTrialNudge,
  getFreeTrialStatus,
  updateFreeTrialStatus,
  getOnDemandSettleNowData,
  getFreeTrialConstants,
  getUnsettledAmount,
  setShowFreeTrialTag,
  settlementType: settlementCategory,
}) {
  useInjectReducer({ key: 'onDemandSettlements', reducer });
  useInjectSaga({ key: 'onDemandSettlements', saga });

  // const { isOnDemandEligible } = useContext(MerchantContext);

  const query = useQuery();
  const env = query.get('env');
  // const openSettleNowModal = query.get('settleNow');

  // adding this to test on demand settlements on mobile app
  const minimumEligibleAmount =
    +getItemFromLocalStorage('merchantId') === ON_DEMAND_TEST_MID
      ? ON_DEMAND_TEST_MINIMUM_ELIGIBLE_AMOUNT
      : MINIMUM_ELIGIBLE_AMOUNT;

  const {
    isLoading,
    holidaysData,
    unSettledAmountData,
    eligibleFundsData,
    filters,
    filtersForDisplay,
    pagination = {},
  } = onDemandSettlements;

  const [tableData, updateTableData] = useState({});
  const [openModal, resetModal] = useState('');
  const [modalData, resetModalData] = useState({});

  const [onDemandStatus, resetOnDemandStatus] = useState('');
  const [onDemandDailyLimit, resetOnDemandDailyLimit] = useState('');
  // const [maxDailyLimitLoader, resetMaxDailyLimitLoader] = useState(false);

  const [remainingSettlementsLimit, resetRemainingSettlementsLimit] = useState(
    'XXXX',
  );
  const [onDemandRate, resetOnDemandRate] = useState({});
  const [onDemandLimitLoader, resetOnDemandLimitLoader] = useState(false);

  const [freeTrialObj, setFreeTrialObj] = useState({});
  // const [isEligibleForFreeTrial, setFreeTrialEligibility] = useState(false);
  const [nudgeObj, setNudgeObj] = useState(null);
  const [freeTrialConstants, setFreeTrialConstants] = useState({});
  const [displayOnDemandStatusBanner, setOnDemandStatusBanner] = useState(
    false,
  );

  const isFreeTrialActive =
    freeTrialObj && freeTrialObj.status === ON_DEMAND_FREE_TRIAL_ACTIVE_STATUS;

  const formatTableData = (response = {}) => {
    const { content: data = [] } = response;
    const totalCount = data.length;
    const pageData = JSON.parse(JSON.stringify(data));

    const formattedData = pageData.map(rowData => ({
      ...rowData,
      id: rowData.settlementId,
      initiatedAt: moment(rowData.initiatedAt).format(DATE_AND_TIME_FORMAT),
      settlementId: () => (
        <div className="flex copy-to-clipboard-wrapper">
          <span className="table-content">{rowData.settlementId}</span>
          {rowData.settlementId ? (
            <CustomIcon
              componentName="COPY_TO_CLIPBOARD"
              handleClick={copyToClipboard(rowData.settlementId)}
            />
          ) : (
            ''
          )}
        </div>
      ),
      utr: () => (
        <div className="flex copy-to-clipboard-wrapper">
          <span className="table-content">{rowData.utr}</span>
          {rowData.utr && rowData.utr !== 'N/A' ? (
            <CustomIcon
              componentName="COPY_TO_CLIPBOARD"
              handleClick={copyToClipboard(rowData.utr)}
            />
          ) : (
            PLACEHOLDER_TEXT
          )}
        </div>
      ),
      status: () => (
        <span className={`w-600 ${TX_STATUS_CLASSNAME[rowData.status] || ''}`}>
          {titleCase(rowData.status)}
        </span>
      ),
      additionalCharges: () => (
        <span>
          {addCurrencySymbol(roundOffNumber(rowData.additionalCharges))}
        </span>
      ),
      deductedAmount: () => (
        <span>{addCurrencySymbol(roundOffNumber(rowData.deductedAmount))}</span>
      ),
      settlementAmount: () => (
        <span>
          {addCurrencySymbol(roundOffNumber(rowData.settlementAmount))}
        </span>
      ),
    }));

    const hasPrevEnabled = filters.pageNo !== 1;
    const hasNextEnabled = totalCount >= filters.pageSize;

    updateTableData({
      totalCount,
      currentPageRowData: formattedData || [],
      hasPrevEnabled,
      hasNextEnabled,
    });
  };

  const getTableData = (f = filters) => {
    // make api call
    enableLoader();
    const { appliedFilters, pageNo, pageSize } = f;

    const formattedFilterObj = {
      ...appliedFilters,
      pageNo: pageNo - 1,
      pageSize,
    };

    if (
      appliedFilters.search &&
      appliedFilters.search.type &&
      appliedFilters.search.value &&
      appliedFilters.search.value.trim()
    ) {
      const {
        search: { type, value },
      } = appliedFilters;
      formattedFilterObj[type] = value.trim();
      delete formattedFilterObj.search;
    }

    searchSettlements({
      options: formattedFilterObj,
      cb: formatTableData,
      settlementType: settlementCategory,
    });
  };

  const onPageChange = type => {
    const options = JSON.parse(JSON.stringify(filters));
    switch (type) {
      case 'NEXT':
        options.pageNo += 1;
        break;
      case 'PREV':
        options.pageNo -= 1;
        break;
      default:
        break;
    }
    updateFilters(options);
  };

  const onLimitChange = (e, limitChangeData) => {
    const options = {
      ...filters,
      pageNo: DEFAULT_CURRENT_PAGE,
      pageSize: parseInt(limitChangeData.value, 10),
    };
    updateFilters(options);
    Analytics.track(EVENTS.SETTLEMENTS[ON_DEMAND].PAGE_SIZE, {
      pageSize: limitChangeData.value,
    });
  };

  const {
    currentPageRowData = [],
    hasPrev = false,
    hasNext = false,
  } = tableData;

  // update the on-demand status as per the api response
  const updateOnDemandStatus = response => {
    if (response) {
      const { updatedOn, status } = response;
      // computing the status
      const ODStatus = getOnDemandStatus(response);
      resetOnDemandStatus(ODStatus);
      // computing whether to show the on demand status banner
      if (
        [ACTIVE, DISABLED].includes(status) &&
        showOnDemandStatusBanner(updatedOn)
      )
        setOnDemandStatusBanner(true);
    } else resetOnDemandStatus(NOT_REQUESTED);
  };

  const getOnDemandCardsData = ({ fn, isFreeTrialActiveParam }) => {
    const onDemandFetchedData = { maxDailyLimit: false, eligibleFunds: false };
    // call the unsettled amount data api
    getUnsettledAmount({ settlementType: settlementCategory });
    // call the on-demand max daily limit api call
    // resetMaxDailyLimitLoader(true);
    getOnDemandMaxDailyLimit({
      cb: response => {
        if (response) {
          const { todayLimit = '', remainingLimit = '' } = response;
          resetOnDemandDailyLimit(todayLimit > 0 ? todayLimit : 0);
          resetRemainingSettlementsLimit(
            remainingLimit > 0 ? remainingLimit : 0,
          );
        }
        // resetMaxDailyLimitLoader(false);
        // for handling settle now case
        onDemandFetchedData.maxDailyLimit = true;
        if (fn && onDemandFetchedData.eligibleFunds) fn();
      },
      errorCb: () => {
        // resetMaxDailyLimitLoader(false);
        if (fn) disableLoader();
      },
      settlementType: settlementCategory,
    });

    // call the on-demand eligibale funds api call
    resetOnDemandLimitLoader(true);

    const queryString = {};
    if (isFreeTrialActive || isFreeTrialActiveParam)
      queryString.freeTrialRequest = true;

    getEligibleFunds({
      queryString,
      cb: response => {
        resetOnDemandLimitLoader(false);
        // for handling settle now case
        if (
          response.eligibleFunds &&
          response.eligibleFunds >= minimumEligibleAmount
        ) {
          onDemandFetchedData.eligibleFunds = true;
          if (fn && onDemandFetchedData.maxDailyLimit) fn();
        } else if (fn) {
          disableLoader();
          showToast(
            new Error(
              `Low balance. You can trigger On-Demand Settlements only for an amount greater than ${
                CURRENCY_SYMBOL.INR
              } ${minimumEligibleAmount}`,
            ),
          );
        }
      },
      errorCb: () => {
        resetOnDemandLimitLoader(false);
        if (fn) disableLoader();
      },
      settlementType: settlementCategory,
    });
  };

  const onWithdrawNow = () => {
    enableLoader();
    getOnDemandCardsData({
      fn: () => {
        disableLoader();
        resetModal(SETTLE_NOW);
      },
    });
    Analytics.track(EVENTS.SETTLEMENTS.SETTLE_NOW);
  };

  const onDemandSettlementStatusCb = (response = {}) => {
    const { OD = {}, FREE_TRIAL = {} } = response;

    const freeTrialActive = FREE_TRIAL.status === ACTIVE;

    // get OD Free Trial constants - if free trial is active or OD status is not requested
    if (!OD.status || freeTrialActive) {
      getFreeTrialConstants({
        cb: res => {
          setFreeTrialConstants(res);
        },
        settlementType: settlementCategory,
      });
    }
    // get the nudge to be shown if any
    getNudgeForFreeTrial();

    // fetch the data for OD card - if OD is active or isSoftApproved or free trial is ACTIVE
    if (OD.status === ACTIVE || OD.isSoftApproved || freeTrialActive) {
      const params = {};
      if (freeTrialActive) params.isFreeTrialActiveParam = true;
      // if (openSettleNowModal) params.fn = () => resetModal(SETTLE_NOW);
      // get OD cards data
      getOnDemandCardsData(params);
    }

    // update the status of OD and free trial response
    if (OD) updateOnDemandStatus(OD);
    if (freeTrialActive) {
      setFreeTrialObj(FREE_TRIAL);
    } else if (isFreeTrialActive) {
      // reset free trial object if free trial was previously active
      setFreeTrialObj({});
      setShowFreeTrialTag(false);
    }
  };

  const fetchOnDemandPageData = () => {
    // call the on demand settlement status api call
    getOnDemandSettlementStatus({
      cb: onDemandSettlementStatusCb,
      settlementType: settlementCategory,
    });
    // call the on demand rate api
    getSettlementRates({
      cb: response => {
        if (response && response.length > 0) {
          const rates = { HOLIDAY: '', WORKING_DAY: '' };
          // calculating the rate
          response.forEach(obj => {
            const { settlementType, RateType, Rate } = obj;
            if (settlementType === 'ON_DEMAND') {
              if (RateType) {
                if (RateType.type === 'WORKING_DAY') {
                  rates.WORKING_DAY = Rate;
                } else {
                  rates.HOLIDAY = Rate;
                }
              }
            }
          });
          // updating the rates if not coming in response
          if (!rates.HOLIDAY) rates.HOLIDAY = rates.WORKING_DAY;
          if (!rates.WORKING_DAY) rates.WORKING_DAY = rates.HOLIDAY;

          resetOnDemandRate(rates);
        } else {
          // disable the settle now button (add a check on disabled props)
          // an error needs to be shown or not ??
        }
      },
      settlementType: settlementCategory,
    });
  };

  useEffect(() => {
    getTableData(filters);
  }, [filters, env]);

  const getNudgeForFreeTrial = () => {
    getFreeTrialNudge({
      cb: res => {
        const { ODScreenBanner, Nudges } = res;
        if (ODScreenBanner) {
          const onDemandSettlementsNudgeObj = Nudges.find(
            obj => obj.displayScreen === 'ODScreen',
          );
          setNudgeObj(getNudgeObj(onDemandSettlementsNudgeObj));
        }
      },
      settlementType: settlementCategory,
    });
  };

  useEffect(() => {
    getSettlementsHolidays({ settlementType: settlementCategory });
    // fetch on demand page data
    fetchOnDemandPageData();
  }, [env, settlementCategory]);

  const closeModal = () => {
    if (openModal === SETTLE_NOW_SUCCESS) {
      getTableData();
      if (isFreeTrialActive) {
        fetchOnDemandPageData();
      } else {
        getOnDemandCardsData({});
      }
      Analytics.track(EVENTS.SETTLEMENTS.MODAL_CLOSE, { type: openModal });
    } else if (openModal === SETTLE_NOW_ERROR) {
      getTableData();
      Analytics.track(EVENTS.SETTLEMENTS.MODAL_CLOSE, { type: openModal });
    } else if (openModal === SETTLE_NOW)
      Analytics.track(EVENTS.SETTLEMENTS.MODAL_CLOSE, { type: openModal });

    resetModal('');
    resetModalData({});
    disableLoader();
  };

  useEffect(() => {
    if (twoFactorAuthentication) {
      getTableData();
      resetTwoFactorAuthentication(false);
    }
    return () => resetData();
  }, [twoFactorAuthentication]);

  const showTableView =
    [ACTIVE, DISABLED, SOFT_APPROVED].includes(onDemandStatus) ||
    isFreeTrialActive;

  const fetchFreeTrialStatus = () => {
    // getFreeTrialStatus
    getFreeTrialStatus({
      cb: res => {
        if (res.status === ACTIVE) {
          // get the on demand card data
          getOnDemandCardsData({
            isFreeTrialActiveParam: true,
          });
          // get the nudge to be shown if any
          getNudgeForFreeTrial();
          setShowFreeTrialTag(true);
        }
        setFreeTrialObj(res);
      },
      settlementType: settlementCategory,
    });
  };

  const onRequestActivation = () => {
    enableLoader();
    requestOnDemandActivation({
      cb: () => {
        fetchOnDemandPageData();
        resetModal(ACTIVATION_MODAL);
      },
      settlementType: settlementCategory,
    });
    Analytics.track(EVENTS.SETTLEMENTS.REQUEST_ACTIVATION);
  };

  const renderOnDemandStatus = () => {
    if (onDemandStatus === NOT_REQUESTED) {
      return (
        <OnDemandBanner
          requestActivation={onRequestActivation}
          freeTrialConstants={freeTrialConstants}
        />
      );
    }
    return null;
  };

  const settleNow = (values, actions) => {
    Analytics.track(EVENTS.SETTLEMENTS.REQUESTED);
    const payload = { requestedAmount: +values.requestedAmount };
    // if free trial is active then add freeTrial to the payload
    if (isFreeTrialActive) payload.freeTrialRequest = true;
    createOnDemandSettlements({
      data: payload,
      errorCb: () => actions.setSubmitting(false),
      cb: response => {
        actions.setSubmitting(false);

        const {
          status,
          initiatedAt,
          requestId,
          deductedAmount,
          additionalCharges,
          requestedAmount,
        } = response;

        const obj = {
          initiatedAt: moment(initiatedAt).format(DATE_AND_TIME_FORMAT),
          settlementId: requestId,
          deductedAmount: addCurrencySymbol(roundOffNumber(deductedAmount)),
          additionalCharges: `${addCurrencySymbol(
            roundOffNumber(additionalCharges),
          )} ${isFreeTrialActive ? '(Under Free Trial)' : ''}`,
          settledAmount: addCurrencySymbol(roundOffNumber(requestedAmount)),
        };

        // IN_PROGRESS - initiated successfully,
        // REJECTED - do not have eligible funds,
        // FAILED - some techinal error

        let modalName = SETTLE_NOW_ERROR;
        if (status === IN_PROGRESS) {
          modalName = SETTLE_NOW_SUCCESS;
          if (+requestedAmount <= 200000)
            obj.description =
              'You will receive the settlement within few minutes.';
        } else if (status === REJECTED)
          obj.description = 'You have surpassed your on-demand limit';
        else obj.description = 'Failed due to some technical error.';

        resetModal(modalName);
        resetModalData(obj);
      },
      settlementType: settlementCategory,
    });
  };

  const refreshCardData = type => () => {
    if (type === ON_DEMAND_LIMIT) {
      resetOnDemandLimitLoader(true);
      const queryString = {};
      if (isFreeTrialActive) queryString.freeTrialRequest = true;
      getEligibleFunds({
        queryString,
        cb: () => resetOnDemandLimitLoader(false),
        settlementType: settlementCategory,
      });
    }
    Analytics.track(EVENTS.SETTLEMENTS.REFRESH, { type });
  };

  const shouldSettleNowBeDisabled = () => {
    // disable settle now button if -
    // 1. Settlement on hold status is not ACTIVE
    // 2. On Demand Status is DISABLED
    // 3. On Demand Rates is not found
    // 4. EligibleFunds is less than 1000

    let disabledInfoText = '';
    let disableSettleNow = Object.keys(onDemandRate).length === 0;

    const settlementOnHoldStatus = holidaysData && holidaysData.status;

    if (settlementOnHoldStatus !== ACTIVE) {
      disableSettleNow = true;
      disabledInfoText =
        'Your Settlements are on Hold. Please contact care@cashfree.com';
    } else if (onDemandStatus === DISABLED) {
      disableSettleNow = true;
      disabledInfoText =
        'This feature is currently disabled for you. Please contact care@cashfree.com';
    } else if (
      !eligibleFundsData.eligibleFunds ||
      +eligibleFundsData.eligibleFunds < minimumEligibleAmount
    ) {
      disableSettleNow = true;
      disabledInfoText = `Low Balance. Minimum account balance of ${
        CURRENCY_SYMBOL.INR
      } ${minimumEligibleAmount} is required to use On-Demand Settlements`;
    }
    return { disableSettleNow, disabledInfoText };
  };

  const { disableSettleNow, disabledInfoText } = shouldSettleNowBeDisabled();

  const FORMATTED_COLUMN_ID = formatColumns(HEADINGS);

  const noRecordsObj = {
    icon: <CustomIcon componentName="BOX" />,
    text: 'No On-Demand Settlement Initiated.',
    action: (
      <span>
        No settlements initiated. Click{' '}
        <Button
          link
          disabled={disableSettleNow}
          className="link-cta"
          type="button"
          onClick={onWithdrawNow}
          data-event-name="Link_WithdrawNow"
        >
          Withdraw Now
        </Button>{' '}
        to receive your first on-demand settlement.
      </span>
    ),
  };

  const showEndFreeTrialModal = () => resetModal('END_FREE_TRIAL_MODAL');

  const renderOnDemandCards = () => (
    <div className="on-demand-cards-wrapper mb-3">
      <OnDemandSummaryCard
        isFreeTrialActive={isFreeTrialActive}
        freeTrialObj={freeTrialObj}
        eligibleFundsData={eligibleFundsData}
        refreshCardData={refreshCardData}
        remainingSettlementsLimit={remainingSettlementsLimit}
        onDemandLimitLoader={onDemandLimitLoader}
        onWithdrawNow={onWithdrawNow}
        disableSettleNow={disableSettleNow}
        disabledInfoText={disabledInfoText}
        onDemandDailyLimit={onDemandDailyLimit}
      />
      <Conditional if={isFreeTrialActive}>
        <div className="on-demand-card position_relative p-3">
          <Text variant="b14" className="mb-1" strong>
            On-Demand Settlements Activated
          </Text>
          <Text variant="b12" color="bodyLight" className="w-400 font-normal">
            Your On-Demand Settlements is currently active with a limit of{' '}
            {CURRENCY_SYMBOL.INR}{' '}
            {numberWithCommas(freeTrialConstants.odDefaultLimit)}.<br />
            However, you can withdraw upto only {CURRENCY_SYMBOL.INR}{' '}
            {numberWithCommas(freeTrialConstants.odFreeTrialPerTxnLimit)} per
            settlement during the free trial. <br />
            <span className="w-600">End Free Trial to withdraw more.</span>
          </Text>
          <Button
            className="on-demand-status-cta"
            secondary
            onClick={showEndFreeTrialModal}
            data-event-name="Form_OnDemandSettlements_Button_EndFreeTrail"
          >
            End Free Trial
          </Button>
        </div>
      </Conditional>
    </div>
  );

  const endFreeTrail = () => {
    closeModal();
    enableLoader();
    updateFreeTrialStatus({
      data: { status: 'INACTIVE' },
      cb: () => {
        fetchOnDemandPageData();
      },
      settlementType: settlementCategory,
    });
  };

  const handleInfoHover = type => {
    Analytics.track(EVENTS.SETTLEMENTS.INFO_ICON, { type });
  };

  const onNudgeCTAClick = id => {
    if (id === NUDGE_BUTTON_ID.EXTEND_NOW) {
      enableLoader();
      extendFreeTrial({
        cb: fetchFreeTrialStatus,
        settlementType: settlementCategory,
      });
    }
  };

  const settleNowStatusObj = SETTLE_NOW_STATUS_MAPPER[openModal];

  const closeStatusBanner = () => setOnDemandStatusBanner(false);

  return (
    <div className="settlements">
      <Loader active={isLoading} />
      <div>
        <Conditional if={showTableView}>
          <Text variant="b14" color="bodyLight" className="mb-3">
            Showing all On-Demand settlements.{' '}
            <a target="_blank" href={KNOW_MORE_URL}>
              Know more
            </a>
          </Text>
        </Conditional>
        {/* Add the activation banner here */}
        <Conditional if={displayOnDemandStatusBanner}>
          <OnDemandStatusBanner
            status={onDemandStatus}
            onClose={closeStatusBanner}
          />
        </Conditional>
        {/* Add a condition if nudgeObj exists then only show the nudgeAlert */}
        {nudgeObj && (
          <div className="mb-3">
            <NudgeAlert {...nudgeObj} onButtonClick={onNudgeCTAClick} />
          </div>
        )}
        <Conditional if={showTableView}>
          {renderOnDemandCards()}
          <DashboardFilters
            menuSelected={SETTLEMENTS}
            filters={filters}
            filtersForDisplay={filtersForDisplay}
            activeNavigation={ON_DEMAND}
            getTableDataFn={getTableData}
            resetFilterFn={resetFilters}
            updateFilterFn={updateFilters}
            resetPageFn={resetPage}
          />
          <PaginatedTable
            data={currentPageRowData}
            emptyTableComponent={noRecordsObj}
            tableHeading={FORMATTED_COLUMN_ID}
            limit={filters.pageSize}
            limitOptions={DEFAULT_LIMIT_OPTIONS}
            hasPrev={hasPrev}
            hasNext={hasNext}
            currentPage={filters.pageNo}
            currentPageCount={currentPageRowData.length}
            totalCount={pagination.totalElements || 0}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
            fetching={isLoading}
          />
        </Conditional>
        <Conditional if={!showTableView}>{renderOnDemandStatus()}</Conditional>
      </div>
      <Conditional if={openModal === SETTLE_NOW}>
        <SettleNowModal
          open
          limitAmount={
            isFreeTrialActive
              ? freeTrialConstants.odFreeTrialPerTxnLimit
              : eligibleFundsData.eligibleFunds
          }
          remainingLimit={remainingSettlementsLimit}
          rate={
            holidaysData && holidaysData.todayHoliday
              ? onDemandRate.HOLIDAY
              : onDemandRate.WORKING_DAY
          }
          closeModal={closeModal}
          handleFormSubmit={settleNow}
          handleInfoHover={handleInfoHover}
          isFreeTrialActive={isFreeTrialActive}
          unSettledAmountData={unSettledAmountData}
          getOnDemandSettleNowData={getOnDemandSettleNowData}
          settlementCategory={settlementCategory}
        />
      </Conditional>
      {!!settleNowStatusObj && (
        <SettleNowSuccessModal
          closeModal={closeModal}
          settleNowStatusObj={settleNowStatusObj}
          modalData={modalData}
          onDemandStatus={onDemandStatus}
          isFreeTrialActive={isFreeTrialActive}
        />
      )}
      <Conditional if={openModal === ACTIVATION_MODAL}>
        <ActivationModal
          closeModal={closeModal}
          freeTrialConstants={freeTrialConstants}
        />
      </Conditional>
      <Conditional if={openModal === 'END_FREE_TRIAL_MODAL'}>
        <EndFreeTrial
          closeModal={closeModal}
          freeTrialObj={freeTrialObj}
          endFreeTrail={endFreeTrail}
        />
      </Conditional>
    </div>
  );
}

OnDemandSettlements.propTypes = {
  onDemandSettlements: PropTypes.object.isRequired,
  searchSettlements: PropTypes.func.isRequired,
  updateTableData: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  enableLoader: PropTypes.func.isRequired,
  resetPage: PropTypes.func,
  resetFilters: PropTypes.func,
  twoFactorAuthentication: PropTypes.any,
  resetTwoFactorAuthentication: PropTypes.func,
  getSettlementsHolidays: PropTypes.func,
  resetData: PropTypes.func.isRequired,
  getOnDemandSettlementStatus: PropTypes.func.isRequired,
  requestOnDemandActivation: PropTypes.func.isRequired,
  getOnDemandMaxDailyLimit: PropTypes.func.isRequired,
  createOnDemandSettlements: PropTypes.func.isRequired,
  getEligibleFunds: PropTypes.func.isRequired,
  getSettlementRates: PropTypes.func.isRequired,
  disableLoader: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  extendFreeTrial: PropTypes.func.isRequired,
  getFreeTrialNudge: PropTypes.func.isRequired,
  getFreeTrialStatus: PropTypes.func.isRequired,
  updateFreeTrialStatus: PropTypes.func.isRequired,
  getOnDemandSettleNowData: PropTypes.func.isRequired,
  getFreeTrialConstants: PropTypes.func.isRequired,
  getUnsettledAmount: PropTypes.func.isRequired,
  setShowFreeTrialTag: PropTypes.func,
  settlementType: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  onDemandSettlements: makeSelectOnDemandSettlements(),
});

function mapDispatchToProps(dispatch) {
  return {
    searchSettlements: data => dispatch(searchSettlementsAction(data)),
    enableLoader: () => dispatch(enableLoaderAction()),
    updateFilters: data => dispatch(updateFiltersAction(data)),
    resetFilters: data => dispatch(resetFiltersAction(data)),
    resetTwoFactorAuthentication: data =>
      dispatch(resetTwoFactorAuthenticationValue(data)),
    resetPage: data => dispatch(resetPageAction(data)),
    resetData: () => dispatch(resetDataAction()),
    getSettlementsHolidays: data =>
      dispatch(getSettlementsHolidaysAction(data)),
    getOnDemandSettlementStatus: data =>
      dispatch(getOnDemandSettlementStatusAction(data)),
    requestOnDemandActivation: data =>
      dispatch(requestOnDemandActivationAction(data)),
    getOnDemandMaxDailyLimit: data =>
      dispatch(getOnDemandMaxDailyLimitAction(data)),
    createOnDemandSettlements: data =>
      dispatch(createOnDemandSettlementsAction(data)),
    getEligibleFunds: data => dispatch(getEligibleFundsAction(data)),
    getSettlementRates: data => dispatch(getSettlementRatesAction(data)),
    disableLoader: () => dispatch(disableLoaderAction()),
    showToast: data => dispatch(showToastAction(data)),
    extendFreeTrial: data => dispatch(extendFreeTrialAction(data)),
    getFreeTrialNudge: data => dispatch(getFreeTrialNudgeAction(data)),
    getFreeTrialStatus: data => dispatch(getFreeTrialStatusAction(data)),
    updateFreeTrialStatus: data => dispatch(updateFreeTrialStatusAction(data)),
    getOnDemandSettleNowData: data =>
      dispatch(getOnDemandSettleNowDataAction(data)),
    getFreeTrialConstants: data => dispatch(getFreeTrialConstantsAction(data)),
    getUnsettledAmount: data => dispatch(getUnsettledAmountAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(OnDemandSettlements);
